import { EnergyQuestion, EnergyIcon } from '../types/energy.types';

interface EnergyQuestionsProps {
  questions: EnergyQuestion[];
  answers: Record<string, string>;
  onChange: (questionId: string, value: string) => void;
  icons: EnergyIcon[];
}

export const EnergyQuestions = ({
  questions,
  answers,
  onChange,
  icons,
}: EnergyQuestionsProps) => {
  if (questions.length === 0) {
    return (
      <div className="text-gray-2 text-sm">
        Select at least one icon to begin.
      </div>
    );
  }

  // Group questions by icon/hazard type
  const groupedQuestions = questions.reduce(
    (acc, question) => {
      if (!acc[question.icon]) {
        acc[question.icon] = [];
      }
      acc[question.icon].push(question);
      return acc;
    },
    {} as Record<string, EnergyQuestion[]>,
  );

  return (
    <div className="flex flex-col gap-6">
      {Object.entries(groupedQuestions).map(([iconId, iconQuestions]) => {
        const icon = icons.find((i) => i.id === iconId);
        if (!icon) return null;

        return (
          <div
            key={iconId}
            className="border-gray-4 bg-gray-5 rounded-xl border p-4"
          >
            {/* Hazard header */}
            <div className="mb-3 flex items-center gap-2">
              <span className="text-xl">{icon.emoji}</span>
              <div>
                <h3 className="text-primary-1 text-sm font-semibold">
                  {icon.title}
                </h3>
                <p className="text-gray-2 text-xs">{icon.desc}</p>
              </div>
            </div>

            {/* Questions for this hazard */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {iconQuestions.map((q) => (
                <div key={q.id} className="flex flex-col gap-2">
                  <label
                    htmlFor={q.id}
                    className="text-gray-2 min-h-[32px] text-xs font-medium"
                  >
                    {q.label}
                  </label>
                  {q.type === 'select' && q.options ? (
                    <select
                      id={q.id}
                      value={answers[q.id] || q.default || ''}
                      onChange={(e) => onChange(q.id, e.target.value)}
                      className="border-gray-4 focus:border-primary-3 rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 outline-none"
                    >
                      {q.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={q.id}
                      type="text"
                      value={answers[q.id] || ''}
                      onChange={(e) => onChange(q.id, e.target.value)}
                      className="border-gray-4 focus:border-primary-3 rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 outline-none"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
